import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { MedicalItemsTab, PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { MedicalNotesConfigurationHelper } from "@/Patient/Helpers/MedicalNotesConfigurationHelper";
import { EncounterSearchIndexRebuildMode, PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { PatientModelHelper } from "@/Patient/Helpers/PatientModelHelper";
import { MedicalItemSubtypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemSubTypeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { ObstetricalSummary } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicalItemViewMode, MedicalItemViewOrigin } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientMedicalSummaryHandler extends BaseOperationHandler {
    private newNoteDebounce: number;

    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.MedicalSummary.SwitchToDetails, this.SwitchViewToDetails);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged, this.EncounterSearchTermChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.EncounterClick, this.EncounterClick, false);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.EncounterSearchCriteriaDisplayToggle, this.EncounterSearchCriteriaDisplayToggle);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.EncounterDateFromChanged, this.EncounterDateFromChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.EncounterDateToChanged, this.EncounterDateToChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.EncounterMyNotesFilterTermChanged, this.EncounterMyNotesFilterChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.EncounterUnapprovedNotesFilterTermChanged, this.EncounterUnapprovedNotesFilterChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.EncounterClinicVisitNotesFilterTermChanged, this.EncounterClinicVisitNotesFilterChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.ToggleDisplayOnlyUnsigned, this.ToggleDisplayOnlyUnsigned);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.ReorderMedicalItemActiveMedication, this.ReorderMedicalItemActiveMedication);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.ReorderMedicalItemAlertsSpecialNeeds, this.ReorderMedicalItemAlertsSpecialNeeds);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.ReorderMedicalItemSocialHabits, this.ReorderMedicalItemSocialHabits);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.ReorderMedicalItemImmunizations, this.ReorderMedicalItemImmunizations);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.ReorderMedicalItemProblems, this.ReorderMedicalItemProblemsAndDiagnosticts);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.CloseAlertPopup, this.CloseAlertPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.RemoveAlert, this.RemoveAlert);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.ToggleAlert, this.ToggleAlert);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.ToggleVisibilityOfSummaryAlertForPrinting, this.toggleVisibilityOfSummaryAlertForPrinting);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.NewNote, this.NewNote);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.NewNoteWarningContinueNote, this.NewNoteWarningContinue);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.NewNoteWarningStartNewNote, this.NewNoteWarningStartNew);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.NewNoteWarningCancel, this.NewNoteWarningCancel);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.NewNoteNoSpecialityWarningOK, this.NewNoteNoSpecialityWarningOK);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.PrintNotes, this.PrintNotes);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.CloseMedicalItemWarningPopup, this.CloseMedicalItemWarningPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.SaveObstetricalSummary, this.saveObstetricalSummary);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.UpsertVitalSign, this.upsertVitalSign);
        this.RegisterOperation(LocalPatientOperations.MedicalSummary.CancelMedicalItemActionFromOutsideNote, this.cancelMedicalItemActionFromOutsideNote);
        this.newNoteDebounce = -1;
    }

    private cancelMedicalItemActionFromOutsideNote(request: LocalEvent, store: PatientStore): void {
        store.IsMedicalItemFromOutsideNote = false;
    }

    private PrintNotes(request: LocalEvent, store: PatientStore): void {
        const DateFrom = store.PatientMedicalSummaryModels[request.PatientId].get("EncounterSearchDateFrom");
        const DateTo = store.PatientMedicalSummaryModels[request.PatientId].get("EncounterSearchDateTo");

        if (cacheStore.ClinicSettings.EnableDirectPatientProfileEdits) {
            const encounterContexts = MedicalNotesConfigurationHelper.getSpecificConfigAndUpdateNotesFilterSettings()?.EncounterContexts;
            store.ActionsMedicalSummary.GenerateNotesPDF(request.PatientId, DateFrom, DateTo, encounterContexts);
        }
        else {
            store.ActionsMedicalSummary.GenerateNotesPDF(request.PatientId, DateFrom, DateTo);
        }
    }

    private SwitchViewToDetails(request: LocalEvent, store: PatientStore): void {
        const view = request.Get<MedicalItemsTab>("activeTab");
        const itemOrigin = request.Get<MedicalItemViewOrigin>("MedicalItemViewOrigin") ?? MedicalItemViewOrigin.Summary;
        const activePatientID = request.Get<string>("PatientId") ?? request.PatientId;
        const selectedEmrTemplateId = request.Get<string>("selectedEMRTemplateId");
        const isMedicalItemFromOutsideNote = !!request.Get<boolean>("isMedicalItemFromOutsideNote");
        const medicalSummaryModel = store.PatientMedicalSummaryModels[activePatientID];
        const noteModel = store.PatientMedicalNoteModels[activePatientID];

        // set what kind of medical items we'll show
        let patientUI = store.PatientUIModels[activePatientID];
        patientUI = patientUI.set("PatientMedicalItemsActiveTab", view);
        patientUI = patientUI.set("ActiveVerticalTab", PatientTab.MedicalItems);

        let medicalItemsModel = Immutable.fromJS(PatientModelHelper.getDefaultPatientMedicalItemsModel());
        medicalItemsModel = medicalItemsModel.set("ItemView", view);

        patientStore.IsMedicalItemFromOutsideNote = isMedicalItemFromOutsideNote;
        patientStore.IsCreatingNewMedicalItem = request.Get<boolean>("isCreatingNewMedicalItem");
        switch (view) {
            case MedicalItemsTab.Medication:
                break;

            case MedicalItemsTab.VitalSigns:
                let vitalSigns = medicalSummaryModel.get("MedicalItemsVitalSign").toArray();
                if (vitalSigns) {
                    vitalSigns = ArrayHelper.SortDynamic(vitalSigns, "PharmaStartDate", true);
                }

                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(vitalSigns));
                break;

            case MedicalItemsTab.MedicalReactions:
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", medicalSummaryModel.get("MedicalItemsMedicalReaction"));
                break;

            case MedicalItemsTab.RiskFactors:
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", medicalSummaryModel.get("MedicalItemsRiskFactor"));
                break;

            case MedicalItemsTab.Immunization:
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", medicalSummaryModel.get("MedicalItemsImmunization"));
                break;

            case MedicalItemsTab.FamilyHistory:
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", medicalSummaryModel.get("MedicalItemsFamilyHistory"));
                break;

            case MedicalItemsTab.Alerts:
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", medicalSummaryModel.get("MedicalItemsAlertAndSpecialNeed"));
                break;
            case MedicalItemsTab.MedicalProblem:
                let allMedicalProblems: MedicalItemProblemAndCondition[] = [];
                allMedicalProblems.push(...medicalSummaryModel.get("MedicalItemsProblemsOnGoing").toArray());
                allMedicalProblems.push(...medicalSummaryModel.get("MedicalItemsProblemsHistory").toArray());
                allMedicalProblems.push(...medicalSummaryModel.get("MedicalItemsSurgeryHistory").toArray());
                allMedicalProblems.push(...medicalSummaryModel.get("MedicalItemsPsychiatricHistory").toArray());
                allMedicalProblems.push(...medicalSummaryModel.get("MedicalItemsGynecologicalHistory").toArray());

                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allMedicalProblems));
                break;

            case MedicalItemsTab.ScreeningTest:
                let screeningTests = medicalSummaryModel.get("MedicalItemsScreeningTest").map(x => MyleHelper.CloneObject(x)).toArray();
                screeningTests = MedicalItemScreeningTest.sortEmrTemplates(screeningTests)
                    .reduce((prev, next) => prev.concat(next));

                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", screeningTests.toImmutable());
                break;
            default:
                break;
        }

        medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, itemOrigin, medicalSummaryModel, (noteModel != null) ? noteModel.get("Encounter").Id : null, store.ActionsMedicalSummary.LoadEMRTemplateComments);
        store.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
        store.PatientUIModels[activePatientID] = patientUI;

        if (!IsNullOrUndefined(selectedEmrTemplateId)) {
            window.setTimeout(() =>
                sessionStore.dispatch(LocalPatientOperations.MedicalItems.SelectEMRTemplate, {
                    value: selectedEmrTemplateId
                }));
        }
    }

    private EncounterSearchTermChanged(request: LocalEvent, store: PatientStore): void {
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        let value = request.Get<string>("value");
        medicalSummaryModel = medicalSummaryModel.set("EncounterSearchTerm", value);
        medicalSummaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(medicalSummaryModel, EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange);

        if (!medicalSummaryModel.get("EncounterFormsTitles"))
            store.ActionsMedicalSummary.loadEncounterFormsTitles(request.PatientId);

        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private EncounterClick(request: LocalEvent, store: PatientStore): void {
        const encounterId = request.Get<string>("value");
        const openInEditMode = request.Get<boolean>("openInEditMode");
        const isMedicalItemFromOutsideNote = !!request.Get<boolean>("isMedicalItemFromOutsideNote");

        const encounterEditContext = store.PatientUIModels[request.PatientId].get("EditEncounterIdContext");
        const currentPatientId = store.getCurrentPatientContactId();

        store.IsMedicalItemFromOutsideNote = isMedicalItemFromOutsideNote;
        store.IsLoading = true;
        if (openInEditMode == true || encounterId == encounterEditContext)
            store.ActionsMedicalNote.LoadEncounterForEdit(encounterId);
        else
            store.ActionsMedicalNote.LoadEncounterForView(encounterId, currentPatientId);
    }

    private EncounterSearchCriteriaDisplayToggle(request: LocalEvent, store: PatientStore): void {
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        medicalSummaryModel = medicalSummaryModel.set("IsSearchCriteriaExpanded", !medicalSummaryModel.get("IsSearchCriteriaExpanded"));
        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private EncounterDateFromChanged(request: LocalEvent, store: PatientStore): void {
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        let value = request.Get<Date>("value");
        medicalSummaryModel = medicalSummaryModel.set("EncounterSearchDateFrom", value);
        medicalSummaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(medicalSummaryModel, EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange);
        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private EncounterDateToChanged(request: LocalEvent, store: PatientStore): void {
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        let value = request.Get<Date>("value");
        medicalSummaryModel = medicalSummaryModel.set("EncounterSearchDateTo", value);
        medicalSummaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(medicalSummaryModel, EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange);

        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private EncounterMyNotesFilterChanged(request: LocalEvent, store: PatientStore): void {
        const patientId = request.PatientId;
        const value = request.Get<boolean>("value");

        // update medical summary
        let medicalSummaryModel = store.PatientMedicalSummaryModels[patientId]
            .set("EncounterFilterMyNotes", value);
        medicalSummaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(medicalSummaryModel, EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange);

        store.PatientMedicalSummaryModels[patientId] = medicalSummaryModel;
    }

    private EncounterUnapprovedNotesFilterChanged(request: LocalEvent, store: PatientStore): void {
        const patientId = request.PatientId;
        const value = request.Get<boolean>("value");

        // update medical summary
        let medicalSummaryModel = store.PatientMedicalSummaryModels[patientId]
            .set("IsDisplayingOnlyUnsigned", value);
        medicalSummaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(medicalSummaryModel, EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange);

        store.PatientMedicalSummaryModels[patientId] = medicalSummaryModel;
    }

    private EncounterClinicVisitNotesFilterChanged(request: LocalEvent, store: PatientStore): void {
        const patientId = request.PatientId;
        const value = request.Get<boolean>("value");

        // update medical summary
        let medicalSummaryModel = store.PatientMedicalSummaryModels[patientId]
            .set("IsDisplayOnlyClinicVisitNotes", value);
        medicalSummaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(medicalSummaryModel, EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange);

        store.PatientMedicalSummaryModels[patientId] = medicalSummaryModel;
    }

    private ToggleDisplayOnlyUnsigned(request: LocalEvent, store: PatientStore): void {
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        medicalSummaryModel = medicalSummaryModel.set("IsDisplayingOnlyUnsigned", !medicalSummaryModel.get("IsDisplayingOnlyUnsigned"));
        medicalSummaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(medicalSummaryModel, EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange);
        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private ReorderMedicalItemActiveMedication(request: LocalEvent, store: PatientStore): void {
        let itemId = request.Get<string>("itemId");
        let newIndex = request.Get<number>("itemIndex"); // new zero based position in list
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        // apply new ordre to all elements
        let medications: MedicationModel[] = medicalSummaryModel.get("FilteredMedicalItemsMedicationAndPrescription").toArray();
        let tmpmedications = <MedicalItemMedicationAndPrescription[]>PatientMedicalSummaryHelper.ReorderMedicalItems(medications.map(x => x.Model), itemId, newIndex);

        // send new order to backend
        store.ActionsMedicalSummary.ReorderEMRTemplateItems(request.PatientId, [MedicalItemTypeEnum.MedicationsAndPrescriptions], tmpmedications);

        // update model
        medications = tmpmedications.map(x => new MedicationModel(x));
        medicalSummaryModel = medicalSummaryModel.set("FilteredMedicalItemsMedicationAndPrescription", Immutable.List(medications));
        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private ReorderMedicalItemAlertsSpecialNeeds(request: LocalEvent, store: PatientStore): void {
        let itemId = request.Get<string>("itemId");
        let newIndex = request.Get<number>("itemIndex"); // new zero based position in list
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        // apply new ordre to all elements
        let alerts: MedicalItemAlertsAndSpecialNeeds[] = medicalSummaryModel.get("MedicalItemsAlertAndSpecialNeed").toArray();
        alerts = <MedicalItemAlertsAndSpecialNeeds[]>PatientMedicalSummaryHelper.ReorderMedicalItems(alerts, itemId, newIndex);

        // send new order to backend
        store.ActionsMedicalSummary.ReorderEMRTemplateItems(request.PatientId, [MedicalItemTypeEnum.AlertsAndSpecialNeeds], alerts);

        // update model
        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsAlertAndSpecialNeed", Immutable.List(alerts));
        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private ReorderMedicalItemSocialHabits(request: LocalEvent, store: PatientStore): void {
        let itemId = request.Get<string>("itemId");
        let newIndex = request.Get<number>("itemIndex"); // new zero based position in list
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        // apply new ordre to all elements
        let riskFactors: MedicalItemRiskFactor[] = medicalSummaryModel.get("MedicalItemsRiskFactor").toArray();

        riskFactors = <MedicalItemRiskFactor[]>PatientMedicalSummaryHelper.ReorderMedicalItems(riskFactors, itemId, newIndex);

        // send new order to backend
        store.ActionsMedicalSummary.ReorderEMRTemplateItems(request.PatientId, [MedicalItemTypeEnum.SocialHabitsAndRiskFactors], riskFactors);

        // update model
        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsRiskFactor", Immutable.List(riskFactors));
        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private ReorderMedicalItemImmunizations(request: LocalEvent, store: PatientStore): void {
        let itemId = request.Get<string>("itemId");
        let newIndex = request.Get<number>("itemIndex"); // new zero based position in list
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        // apply new ordre to all elements
        let immunizations: MedicalItemImmunization[] = medicalSummaryModel.get("MedicalItemsImmunization").toArray();
        immunizations = <MedicalItemImmunization[]>PatientMedicalSummaryHelper.ReorderMedicalItems(immunizations, itemId, newIndex);

        // send new order to backend
        store.ActionsMedicalSummary.ReorderEMRTemplateItems(request.PatientId, [MedicalItemTypeEnum.Immunization], immunizations);

        // update model
        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsImmunization", Immutable.List(immunizations));
        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private ReorderMedicalItemProblemsAndDiagnosticts(request: LocalEvent, store: PatientStore): void {
        let itemId = request.Get<string>("itemId");
        let newIndex = request.Get<number>("itemIndex"); // new zero based position in list
        let startGroup = request.Get<string>("startGroup");
        let destinationGroup = request.Get<string>("destinationGroup");

        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        let medicalProblems: MedicalItem[] = [];
        let newSubType: MedicalItemSubtypeEnum;

        switch (destinationGroup) {
            case "problems_ongoing":
                medicalProblems = medicalSummaryModel.get("MedicalItemsProblemsOnGoing").toArray();
                newSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing;
                break;
            case "problems_historical":
                medicalProblems = medicalSummaryModel.get("MedicalItemsProblemsHistory").toArray();
                newSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Past;
                break;
            case "problems_surgical":
                medicalProblems = medicalSummaryModel.get("MedicalItemsSurgeryHistory").toArray();
                newSubType = MedicalItemSubtypeEnum.ProblemAndCondition_SurgicalPast;
                break;
            case "problems_psychiatric":
                medicalProblems = medicalSummaryModel.get("MedicalItemsPsychiatricHistory").toArray();
                newSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Psychiatric;
                break;
            case "problems_gynecological":
                medicalProblems = medicalSummaryModel.get("MedicalItemsGynecologicalHistory").toArray();
                newSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Gynecology;
                break;
        }

        if (medicalProblems) {
            let itemsToUpdate: MedicalItem[] = [];

            // same group reorder
            if ((startGroup == null || startGroup == undefined) || startGroup == destinationGroup) {
                medicalProblems = medicalProblems.map(x => { x.EMRTemplateMedicalItemSubType = newSubType; return x; }); // ensure all items are of correct subtype
                medicalProblems = PatientMedicalSummaryHelper.ReorderGroupedMedicalItems(medicalProblems, itemId, newIndex); // use qualifiedDetailID... emritemid not unique here
            }
            // different group reorder
            else {
                // get and remove item from old group
                let oldMedicalProblemGroup: MedicalItem[] = [];
                let oldSubType: MedicalItemSubtypeEnum = null;
                switch (startGroup) {
                    case "problems_ongoing":
                        oldMedicalProblemGroup = medicalSummaryModel.get("MedicalItemsProblemsOnGoing").toArray();
                        oldSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing;

                        break;
                    case "problems_historical":
                        oldMedicalProblemGroup = medicalSummaryModel.get("MedicalItemsProblemsHistory").toArray();
                        oldSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Past;

                        break;
                    case "problems_surgical":
                        oldMedicalProblemGroup = medicalSummaryModel.get("MedicalItemsSurgeryHistory").toArray();
                        oldSubType = MedicalItemSubtypeEnum.ProblemAndCondition_SurgicalPast;

                        break;
                    case "problems_psychiatric":
                        oldMedicalProblemGroup = medicalSummaryModel.get("MedicalItemsPsychiatricHistory").toArray();
                        oldSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Psychiatric;
                        break;
                    case "problems_gynecological":
                        oldMedicalProblemGroup = medicalSummaryModel.get("MedicalItemsGynecologicalHistory").toArray();
                        oldSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Gynecology;
                        break;
                }
                let switchedItems = oldMedicalProblemGroup.filter(x => x.EMRTemplateId == itemId);

                // let switchedItem = oldMedicalProblemGroup[oldIndex];
                oldMedicalProblemGroup = oldMedicalProblemGroup.filter(x => x.EMRTemplateId != itemId);
                oldMedicalProblemGroup = oldMedicalProblemGroup.map(x => { x.EMRTemplateMedicalItemSubType = oldSubType; return x; }); // ensure all items are of correct subtype
                oldMedicalProblemGroup = PatientMedicalSummaryHelper.ReApplySortingAndNumbering(oldMedicalProblemGroup as MedicalItem[]);

                itemsToUpdate = itemsToUpdate.concat(oldMedicalProblemGroup as MedicalItem[]);

                switch (startGroup) {
                    case "problems_ongoing":
                        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsProblemsOnGoing", Immutable.List(oldMedicalProblemGroup as MedicalItemProblemAndCondition[]));
                        break;
                    case "problems_historical":
                        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsProblemsHistory", Immutable.List(oldMedicalProblemGroup as MedicalItemProblemAndCondition[]));
                        break;
                    case "problems_surgical":
                        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsSurgeryHistory", Immutable.List(oldMedicalProblemGroup as MedicalItemProblemAndCondition[]));
                        break;
                    case "problems_psychiatric":
                        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsPsychiatricHistory", Immutable.List(oldMedicalProblemGroup as MedicalItemProblemAndCondition[]));
                        break;
                    case "problems_gynecological":
                        medicalSummaryModel = medicalSummaryModel.set("MedicalItemsGynecologicalHistory", Immutable.List(oldMedicalProblemGroup as MedicalItemProblemAndCondition[]));
                        break;
                }

                // set item in new group
                switchedItems.forEach(switchedItem => {
                    switchedItem.EMRTemplateMedicalItemSubType = newSubType;
                    switchedItem.EMRTemplateSortIndex = newIndex;
                    medicalProblems.push(switchedItem);
                });

                medicalProblems = medicalProblems.map(x => { x.EMRTemplateMedicalItemSubType = newSubType; return x; }); // ensure all items are of correct subtype
                medicalProblems = PatientMedicalSummaryHelper.ReorderGroupedMedicalItems(medicalProblems, itemId, newIndex); // use qualifiedDetailID... emritemid not unique here
            }

            itemsToUpdate = itemsToUpdate.concat(medicalProblems as MedicalItem[]);

            // set final destination group model
            switch (destinationGroup) {
                case "problems_ongoing":
                    medicalSummaryModel = medicalSummaryModel.set("MedicalItemsProblemsOnGoing", Immutable.List(medicalProblems as MedicalItemProblemAndCondition[]));
                    break;
                case "problems_historical":
                    medicalSummaryModel = medicalSummaryModel.set("MedicalItemsProblemsHistory", Immutable.List(medicalProblems as MedicalItemProblemAndCondition[]));
                    break;
                case "problems_surgical":
                    medicalSummaryModel = medicalSummaryModel.set("MedicalItemsSurgeryHistory", Immutable.List(medicalProblems as MedicalItemProblemAndCondition[]));
                    break;
                case "problems_psychiatric":
                    medicalSummaryModel = medicalSummaryModel.set("MedicalItemsPsychiatricHistory", Immutable.List(medicalProblems as MedicalItemProblemAndCondition[]));
                    break;
                case "problems_gynecological":
                    medicalSummaryModel = medicalSummaryModel.set("MedicalItemsGynecologicalHistory", Immutable.List(medicalProblems as MedicalItemProblemAndCondition[]));
                    break;
            }

            // send new order to backend
            store.ActionsMedicalSummary.ReorderEMRTemplateItems(request.PatientId, [MedicalItemTypeEnum.PastAndOngoingProblemsAndConditions], itemsToUpdate);
        }

        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private CloseAlertPopup(request: LocalEvent, store: PatientStore): void {
        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        medicalSummaryModel = medicalSummaryModel.set("ShowAlertPopup", false);

        let alertsToRemove = medicalSummaryModel.get("AlertToRemoveEMRTemplateIds").toArray();

        if (alertsToRemove && alertsToRemove.length > 0) {
            store.ActionsMedicalSummary.MedicalALertActivation([], alertsToRemove);
        }

        medicalSummaryModel = medicalSummaryModel.set("AlertToRemoveEMRTemplateIds", Immutable.List([]));

        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private RemoveAlert(request: LocalEvent, store: PatientStore): void {
        let emrTemplateId = request.Get<string>("id");
        let isChecked = request.Get<boolean>("checked");

        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];

        let alertsToRemove = medicalSummaryModel.get("AlertToRemoveEMRTemplateIds").toArray();

        if (isChecked) alertsToRemove.push(emrTemplateId);
        else alertsToRemove = alertsToRemove.filter(x => x != emrTemplateId);

        medicalSummaryModel = medicalSummaryModel.set("AlertToRemoveEMRTemplateIds", Immutable.List(alertsToRemove));

        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
    }

    private toggleVisibilityOfSummaryAlertForPrinting(request: LocalEvent, store: PatientStore): void {
        const emrTemplateId = request.Get<string>("emrTemplateId");
        const isVisibleOnExport = request.Get<boolean>("isVisibleOnExport");
        let itemsModel = store.PatientMedicalItemsModels[request.PatientId];

        if (itemsModel.get("ViewMode") == MedicalItemViewMode.Edit) {
            const medicalItems = itemsModel.get("MedicalItemsList").toArray();
            const isAnyUncommitted = medicalItems.some(p => !p.IsCommitted);
            if (isAnyUncommitted) {
                medicalItems.forEach(p => {
                    p.EMRTemplateIsVisibleOnExport = !p.EMRTemplateIsVisibleOnExport;
                });
                store.PatientMedicalItemsModels[request.PatientId] = itemsModel.set("MedicalItemsList", Immutable.List(medicalItems));
                return;
            }
        }

        store.ActionsMedicalSummary.SetMedicalSummaryVisibilityWhenPrinting(emrTemplateId, isVisibleOnExport);
    }

    private ToggleAlert(request: LocalEvent, store: PatientStore): void {
        let emrTemplateId = request.Get<string>("emrTemplateId");
        let isActivated = request.Get<boolean>("newValue");

        let ids: string[] = [emrTemplateId];

        if (isActivated) {
            store.ActionsMedicalSummary.MedicalALertActivation(ids, []);
        }
        else {
            store.ActionsMedicalSummary.MedicalALertActivation([], ids);
        }

        let detailsModel = store.PatientMedicalItemsModels[request.PatientId];
        let summaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        if (detailsModel) {
            if (detailsModel.get("ViewMode") === MedicalItemViewMode.Edit) {
                const medicalItems = detailsModel.get("MedicalItemsList").toArray();
                const hasUncommittedItems = medicalItems.some(p => !p.IsCommitted);

                if (hasUncommittedItems) {
                    medicalItems.forEach(p => p.EMRTemplateIsDisplayPopup = isActivated);
                    store.PatientMedicalItemsModels[request.PatientId] = detailsModel.set("MedicalItemsList", Immutable.List(medicalItems));

                    return;
                }
            }

            const allItems = detailsModel.get("AllMedicalItemsList").toArray();

            const updatedItems = allItems.map(x => {
                if (x.EMRTemplateId == emrTemplateId)
                    x.EMRTemplateIsDisplayPopup = isActivated;

                return x;
            });

            detailsModel = detailsModel.set("AllMedicalItemsList", Immutable.List(updatedItems));
            detailsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(detailsModel, null, MedicalItemViewOrigin.Summary, summaryModel, (noteModel != null) ? noteModel.get("Encounter").Id : null, store.ActionsMedicalSummary.LoadEMRTemplateComments);

            store.PatientMedicalItemsModels[request.PatientId] = detailsModel;
        }
    }

    private NewNote(request: LocalEvent, store: PatientStore): void {
        const isMedicalItemFromOutsideNote = !!request.Get<boolean>("isMedicalItemFromOutsideNote");

        if (this.newNoteDebounce !== -1) {
            clearTimeout(this.newNoteDebounce);
            this.newNoteDebounce = -1;
        }

        store.IsMedicalItemFromOutsideNote = isMedicalItemFromOutsideNote;

        const activePatientId = request.PatientId;
        let summaryModel = store.PatientMedicalSummaryModels[activePatientId];
        const patientModel = store.PatientModels[activePatientId];

        const defaultSpecialization = sessionStore.getCurrentEmployeeDefaultSpecializationLookupId();
        if (!defaultSpecialization) {
            summaryModel = summaryModel.set("ShowNoteCreationSpecialityErrorWarning", true);
            store.PatientMedicalSummaryModels[activePatientId] = summaryModel;
            sessionStore.showGlobalOverlay(true);
            return;
        }

        const encounters = summaryModel.get("Encounters").toArray();
        const encountersNotSignedOff = encounters.filter(x => x.EncounterRelatedEmployees.some(y => y.EmployeeId == sessionStore.getCurrentEmployee().ContactFacetId) && (x.SignedOffDate == null || x.SignedOffDate == undefined));
        const currentSiteId = sessionStore.getCurrentEmployee().getActiveSite().Model.Id;
        const costCenterId = PatientHelper.getCostCenterId();

        if (encountersNotSignedOff && encountersNotSignedOff.length == 1) {
            summaryModel = summaryModel.set("ShowNewNoteWarning", true);
            summaryModel = summaryModel.set("NewNoteWarningEncounterDate", encountersNotSignedOff[0].ConsultationDate);
            summaryModel = summaryModel.set("NewNoteWarningEncounterId", encountersNotSignedOff[0].Id);
        }
        else {
            this.newNoteDebounce = window.setTimeout(() => {
                if (patientModel != null) {
                    store.ActionsMedicalNote.CreateNote(
                        patientModel.PatientResponse.ContactFacet_id,
                        currentSiteId,
                        defaultSpecialization,
                        null,
                        costCenterId
                    );
                }
            }, 350);
        }

        store.PatientMedicalSummaryModels[activePatientId] = summaryModel;
    }

    private NewNoteWarningContinue(request: LocalEvent, store: PatientStore): void {
        let activePatientId = request.PatientId;
        let summaryModel = store.PatientMedicalSummaryModels[activePatientId];

        store.ActionsMedicalNote.LoadEncounterForView(summaryModel.get("NewNoteWarningEncounterId"));

        this.NewNoteWarningCancel(request, store);
    }

    private NewNoteWarningStartNew(request: LocalEvent, store: PatientStore): void {
        let activePatientId = request.PatientId;
        let patientModel = store.PatientModels[activePatientId];
        let currentSiteId = sessionStore.getCurrentEmployee().getActiveSite().Model.Id;
        let costCenterId = PatientHelper.getCostCenterId();

        if (!(patientModel == null)) {
            store.ActionsMedicalNote.CreateNote(
                patientModel.PatientResponse.ContactFacet_id,
                currentSiteId,
                sessionStore.getCurrentEmployeeDefaultSpecializationLookupId(),
                null,
                costCenterId
            );
        }
    }

    private NewNoteWarningCancel(request: LocalEvent, store: PatientStore): void {
        let activePatientId = request.PatientId;
        let summaryModel = store.PatientMedicalSummaryModels[activePatientId];

        summaryModel = summaryModel.set("ShowNewNoteWarning", false);
        summaryModel = summaryModel.set("NewNoteWarningEncounterDate", null);
        summaryModel = summaryModel.set("NewNoteWarningEncounterId", "");
        sessionStore.hideGlobalOverlay();

        store.PatientMedicalSummaryModels[activePatientId] = summaryModel;
    }

    private NewNoteNoSpecialityWarningOK(request: LocalEvent, store: PatientStore): void {
        let activePatientId = request.PatientId;
        let summaryModel = store.PatientMedicalSummaryModels[activePatientId];

        summaryModel = summaryModel.set("ShowNoteCreationSpecialityErrorWarning", false);
        sessionStore.hideGlobalOverlay();

        store.PatientMedicalSummaryModels[activePatientId] = summaryModel;
    }

    private CloseMedicalItemWarningPopup(request: LocalEvent, store: PatientStore): void {
        let activePatientId = request.PatientId;
        let noteModel = store.PatientMedicalNoteModels[activePatientId];

        noteModel = noteModel.set("MedicalNoteErrorWarningPopup", false);

        store.PatientMedicalNoteModels[activePatientId] = noteModel;
    }

    private saveObstetricalSummary(request: LocalEvent, store: PatientStore): void {
        let obstetricalSummary = request.Get<ObstetricalSummary>("ObstetricalSummary");
        obstetricalSummary.PatientContactFacetId = patientStore.getCurrentPatient().PatientResponse.ContactFacet_id;

        store.ActionsMedicalSummary.saveObstetricalSummary(obstetricalSummary);
    }

    private upsertVitalSign(request: LocalEvent, store: PatientStore): void {
        const vitalSigns = request.Get<MedicalItemVitalSign[]>("vitalSigns");
        if (!vitalSigns)
            return;

        let summaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        summaryModel = summaryModel.set("MedicalItemsVitalSign", Immutable.List(vitalSigns));
        store.PatientMedicalSummaryModels[request.PatientId] = summaryModel;

        let medicalItemsModel = store.PatientMedicalItemsModels[request.PatientId];
        if (medicalItemsModel && medicalItemsModel.get("ItemView") === MedicalItemsTab.VitalSigns) {
            let emptyVitalSigns = medicalItemsModel.get("AllMedicalItemsList")
                ?.filter(p => p.EMRTemplateMedicalItemType === MedicalItemTypeEnum.VitalSigns && p.EMRTemplateId === GuidHelper.Empty())
                ?.map(p => p as MedicalItemVitalSign);

            medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(vitalSigns.concat(emptyVitalSigns?.toArray() ?? [])));

            let itemsToDisplay = PatientMedicalSummaryHelper.GetMedicalItemsToDisplay(medicalItemsModel.get("AllMedicalItemsList"), medicalItemsModel.get("SelectedEMRTemplateId"));
            medicalItemsModel = medicalItemsModel.set("MedicalItemsList", Immutable.List(itemsToDisplay));

            store.PatientMedicalItemsModels[request.PatientId] = medicalItemsModel;
        }
    }
}

export { PatientMedicalSummaryHandler };
